"use client"

import { useState, useEffect } from "react"
import { Box, Typography, Container, Paper } from "@mui/material"
import { Star, AutoAwesome, Whatshot } from "@mui/icons-material"
import { keyframes } from "@mui/system"
import { API_URL } from './config';
import { TeamsPodiumScreen } from "./leaders"

// אנימציות CSS מתקדמות
const sparkle = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
  50% { transform: scale(1.3) rotate(180deg); opacity: 0.7; }
`

const riseFromBelow = keyframes`
  0% {
    transform: translateY(150%);
    opacity: 0;
  }
  100% {
    transform: translateY(0%);
    opacity: 1;
  }
`

const riseUp = keyframes`
  0% {
    transform: translateY(100px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`

const fallDown = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(200vh);
    opacity: 0;
  }
`

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-15px); }
  60% { transform: translateY(-8px); }
`

const glow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 30px rgba(255, 215, 0, 0.6), 0 0 60px rgba(255, 215, 0, 0.4);
    filter: brightness(1);
  }
  50% { 
    box-shadow: 0 0 50px rgba(255, 215, 0, 0.9), 0 0 100px rgba(255, 215, 0, 0.6);
    filter: brightness(1.2);
  }
`

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50% { transform: translateY(-25px) rotate(5deg); }
`

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5) rotate(-10deg);
  }
  to {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
`

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`

const gentlePulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.02); opacity: 1; }
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const oneTimeSpin = keyframes`
  0% { transform: rotate(0deg); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: rotate(360deg); opacity: 1; }
`

const rainbow = keyframes`
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
`

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

const swayBeam = keyframes`
  0%   { transform: rotate(-25deg) translateX(-40px); }
  50%  { transform: rotate(25deg) translateX(40px); }
  100% { transform: rotate(-25deg) translateX(-40px); }
`

const explosion = keyframes`
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(2) rotate(180deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(4) rotate(360deg);
    opacity: 0;
  }
`

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
  20%, 40%, 60%, 80% { transform: translateX(10px); }
`

// אנימציות חדשות לדף העולה
const pageRise = keyframes`
  0% { transform: translateY(100vh); }
  100% { transform: translateY(0); }
`

const textFadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`

const contentFadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`

// אנימציות חדשות מטורפות לקבוצה המנצחת
const epicEntrance = keyframes`
  0% {
    transform: scale(0) rotate(-180deg) translateY(100px);
    opacity: 0;
    filter: blur(20px);
  }
  50% {
    transform: scale(1.2) rotate(0deg) translateY(-20px);
    opacity: 0.8;
    filter: blur(5px);
  }
  100% {
    transform: scale(1) rotate(0deg) translateY(0);
    opacity: 1;
    filter: blur(0px);
  }
`

const winnerEntrance = keyframes`
  0% {
    transform: scale(0) rotate(-360deg) translateY(-100px);
    opacity: 0;
    filter: blur(30px);
  }
  30% {
    transform: scale(0.8) rotate(-180deg) translateY(-50px);
    opacity: 0.5;
    filter: blur(15px);
  }
  70% {
    transform: scale(1.3) rotate(0deg) translateY(10px);
    opacity: 0.9;
    filter: blur(5px);
  }
  100% {
    transform: scale(1) rotate(0deg) translateY(0);
    opacity: 1;
    filter: blur(0px);
  }
`

const subtleGlow = keyframes`
  0%, 100% {
    box-shadow: 
      0 0 20px rgba(255, 255, 255, 0.3),
      0 0 40px rgba(255, 215, 0, 0.2),
      inset 0 0 20px rgba(255, 255, 255, 0.1);
  }
  50% {
    box-shadow: 
      0 0 30px rgba(255, 255, 255, 0.5),
      0 0 60px rgba(255, 215, 0, 0.4),
      inset 0 0 30px rgba(255, 255, 255, 0.2);
  }
`

const winnerGlow = keyframes`
  0%, 100% {
    box-shadow: 
      0 0 50px rgba(255, 215, 0, 0.8),
      0 0 100px rgba(255, 215, 0, 0.6),
      0 0 150px rgba(255, 215, 0, 0.4),
      inset 0 0 40px rgba(255, 255, 255, 0.3);
  }
  50% {
    box-shadow: 
      0 0 80px rgba(255, 215, 0, 1),
      0 0 150px rgba(255, 215, 0, 0.8),
      0 0 200px rgba(255, 215, 0, 0.6),
      inset 0 0 60px rgba(255, 255, 255, 0.4);
  }
`

const particleFloat = keyframes`
  0% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) translateX(${Math.random() * 40 - 20}px) rotate(360deg);
    opacity: 0;
  }
`

const energyWave = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
`

const textGlitch = keyframes`
  0%, 100% {
    transform: translate(0);
  }
  10% {
    transform: translate(-1px, 1px);
  }
  20% {
    transform: translate(-1px, -1px);
  }
  30% {
    transform: translate(1px, 1px);
  }
  40% {
    transform: translate(1px, -1px);
  }
`

const lightning = keyframes`
  0%, 90%, 100% {
    opacity: 0;
    transform: scaleX(0);
  }
  5%, 85% {
    opacity: 1;
    transform: scaleX(1);
  }
`

const magicSparkle = keyframes`
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.5) rotate(180deg);
    opacity: 1;
  }
  100% {
    transform: scale(0) rotate(360deg);
    opacity: 0;
  }
`

// רכיב פנסים
const LightBeam = ({ left = true, color = "rgba(255,255,255,0.2)" }) => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      [left ? "left" : "right"]: 0,
      zIndex: 80,
      pointerEvents: "none",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    {/* גוף הפנס */}
    <Box
      sx={{
        width: 40,
        height: 25,
        backgroundColor: "#111",
        borderRadius: "4px",
        boxShadow: "0 0 15px rgba(255,255,255,0.4)",
        mb: 1,
      }}
    />
    {/* קרן האור */}
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: "250px solid transparent",
        borderRight: "250px solid transparent",
        borderBottom: `800px solid ${color}`,
        animation: `${swayBeam} 5s ease-in-out infinite`,
        filter: "blur(10px)",
        transformOrigin: "top center",
        opacity: 0.9,
      }}
    />
  </Box>
)

// רכיב זיקוקים מתקדם
const Firework = ({ delay = 0, size = 35, intense = false }) => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay)
    return () => clearTimeout(timer)
  }, [delay])

  if (!show) return null

  return (
    <Box
      sx={{
        position: "absolute",
        animation: intense
          ? `${explosion} 2s ease-out, ${rainbow} 1s infinite`
          : `${fadeIn} 1s ease-in, ${sparkle} 2s infinite, ${rainbow} 4s infinite`,
        animationDelay: `0s, ${delay}ms, 1s`,
      }}
    >
      <AutoAwesome
        sx={{
          color: "#FFD700",
          fontSize: intense ? size * 2 : size,
          filter: `drop-shadow(0 0 ${intense ? 20 : 10}px #FFD700)`,
        }}
      />
    </Box>
  )
}

// רכיב פיצוץ גדול
const BigExplosion = ({ delay = 0 }) => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay)
    return () => clearTimeout(timer)
  }, [delay])

  if (!show) return null

  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        animation: `${explosion} 3s ease-out`,
        zIndex: 100,
      }}
    >
      {[...Array(12)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            animation: `${explosion} 2s ease-out`,
            animationDelay: `${i * 0.1}s`,
            transform: `rotate(${i * 30}deg) translateX(${50 + i * 10}px)`,
          }}
        >
          <Whatshot
            sx={{
              color: ["#FF4500", "#FFD700", "#FF69B4", "#00FFFF", "#32CD32"][i % 5],
              fontSize: 60,
              filter: "drop-shadow(0 0 15px currentColor)",
            }}
          />
        </Box>
      ))}
    </Box>
  )
}

// רכיב כוכבים מנצנצים מתקדם
const SparkleEffect = ({ intense = false }) => {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {[...Array(intense ? 60 : 30)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `${sparkle} ${2 + Math.random() * 4}s infinite, ${float} ${3 + Math.random() * 2}s infinite`,
            animationDelay: `${Math.random() * 3}s, ${Math.random() * 2}s`,
          }}
        >
          <Star
            sx={{
              color: ["#FFD700", "#FF69B4", "#00FFFF", "#FF4500", "#32CD32"][Math.floor(Math.random() * 5)],
              fontSize: Math.random() * (intense ? 40 : 25) + 15,
              filter: "drop-shadow(0 0 8px currentColor)",
            }}
          />
        </Box>
      ))}
    </Box>
  )
}

// רכיב חלקיקי אנרגיה מתקדם
const EnergyParticles = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 5,
      }}
    >
      {[...Array(40)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            bottom: `${Math.random() * 20}%`,
            left: `${Math.random() * 100}%`,
            width: `${Math.random() * 6 + 3}px`,
            height: `${Math.random() * 6 + 3}px`,
            background: `radial-gradient(circle, ${
              ["#FFD700", "#FF69B4", "#00FFFF", "#FF4500", "#32CD32", "#9400D3"][Math.floor(Math.random() * 6)]
            }, transparent)`,
            borderRadius: "50%",
            animation: `${particleFloat} ${4 + Math.random() * 3}s infinite linear`,
            animationDelay: `${Math.random() * 3}s`,
            filter: "blur(1px)",
          }}
        />
      ))}
    </Box>
  )
}

// רכיב פודיום מתקדם
const PodiumStep = ({ winner, height, delay, shouldFall = false }) => {
  const [showCard, setShowCard] = useState(false)
  const [showPodium, setShowPodium] = useState(false)
  const [startBounce, setStartBounce] = useState(false)
  const [falling, setFalling] = useState(false)
  const isFirst = winner.position === 1

  useEffect(() => {
    const showTimer = setTimeout(() => setShowCard(true), delay)
    const bounceTimer = setTimeout(() => setStartBounce(true), delay + 2000)
    const podiumTimer = setTimeout(() => setShowPodium(true), delay + 1000)

    if (shouldFall) {
      const fallTimer = setTimeout(() => setFalling(true), delay + 14000)
      return () => clearTimeout(fallTimer)
    }

    return () => {
      clearTimeout(showTimer)
      clearTimeout(bounceTimer)
      clearTimeout(podiumTimer)
    }
  }, [delay, shouldFall])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        zIndex: 10,
        animation: falling ? `${fallDown} 2s ease-in forwards` : "none",
      }}
    >
      {showCard && (
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: `radial-gradient(circle at 30% 30%, ${winner.color}, ${winner.color}BB, #00000033)`,
            border: `4px solid ${winner.color}`,
            boxShadow: `
              inset 4px 4px 10px rgba(255,255,255,0.3),
              inset -4px -4px 10px rgba(0,0,0,0.3),
              0 8px 20px rgba(0,0,0,0.3)
            `,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: startBounce ? `bounce 2.5s infinite` : "none",
            position: "relative",
            mx: "auto",
            my: 3,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: 48,
              fontWeight: "bold",
              color: winner.color,
              textShadow: `
                -2px -2px 2px rgba(0, 0, 0, 0.4),
                2px 2px 2px rgba(255, 255, 255, 0.3)
              `,
              userSelect: "none",
              lineHeight: 1,
            }}
          >
            {winner.id}
          </Typography>
        </Box>
      )}

      <Paper
        elevation={20}
        sx={{
          width: 340,
          height: height,
          background: `repeating-linear-gradient(135deg, ${winner.color}, ${winner.color} 10px, ${winner.color}99 10px, ${winner.color}99 20px)`,
          border: `3px solid ${winner.color}`,
          borderRadius: "20px 20px 0 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          position: "relative",
          transform: "perspective(1000px) rotateX(12deg)",
          boxShadow: `
            0 20px 30px rgba(0,0,0,0.4),
            inset 0 -10px 15px rgba(0,0,0,0.3),
            inset 0 5px 10px rgba(255,255,255,0.2)
          `,
          animation: showPodium ? `${fadeInUp} 1.2s ease-out` : "none",
          opacity: showPodium ? 1 : 0,
          overflow: "hidden",
        }}
      >
        {/* זוהר כללי */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at center, ${winner.color}20, transparent 70%)`,
            animation: `${pulse} 5s infinite`,
            zIndex: 1,
          }}
        />

        {/* ✨ ניקוד */}
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{
            mb: winner.id === 1 ? 6 : 3,
            zIndex: 3,
            color: "white",
            textShadow: "2px 2px 6px rgba(0,0,0,0.5)",
            animation: `${pulse} 2.5s infinite`,
          }}
        >
          ✨{" "}
          <Box
            component="span"
            sx={{
              background: "linear-gradient(135deg, #1e3c72, #764ba2, #f093fb)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {winner.score}
          </Box>{" "}
          ✨
        </Typography>

        {/* ☎️ טלפון */}
        <Box
          sx={{
            background: "rgba(255,255,255,0.12)",
            borderRadius: 2,
            px: 3,
            py: 1,
            mb: winner.id === 1 ? 5 : 2,
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.3)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
            zIndex: 2,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              color: "#fff",
              textShadow: "1px 1px 4px rgba(0,0,0,0.7)",
            }}
          >
            📞 {winner.name || winner.phone}
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

// רכיב הקבוצה המנצחת המשופר והמטורף!
const WinningTeamContent = ({ teams, show }) => {
  const [showElements, setShowElements] = useState({
    background: false,
    winner: false,
    others: false,
    effects: false,
  })

  useEffect(() => {
    if (show && teams && teams.length > 0) {
      const timers = [
        setTimeout(() => setShowElements((prev) => ({ ...prev, background: true })), 500),
        setTimeout(() => setShowElements((prev) => ({ ...prev, winner: true })), 1000),
        setTimeout(() => setShowElements((prev) => ({ ...prev, others: true })), 1800),
        setTimeout(() => setShowElements((prev) => ({ ...prev, effects: true })), 2500),
      ]
      return () => timers.forEach(clearTimeout)
    }
  }, [show, teams])

  if (!show || !teams || teams.length === 0) return null

  // מיון הקבוצות לפי ניקוד
  const sortedTeams = [...teams].sort((a, b) => b.score - a.score)
  const winner = sortedTeams[0]
  const others = sortedTeams.slice(1)

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999,
        overflow: "hidden",
        padding: 2,
      }}
    >
      {/* רקע עדין */}
      {showElements.background && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 30% 30%, rgba(255, 215, 0, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 70%, rgba(255, 105, 180, 0.15) 0%, transparent 50%),
              linear-gradient(135deg, 
                rgba(30, 60, 114, 0.9) 0%, 
                rgba(42, 82, 152, 0.9) 25%, 
                rgba(102, 126, 234, 0.9) 50%, 
                rgba(118, 75, 162, 0.9) 75%, 
                rgba(240, 147, 251, 0.9) 100%)
            `,
            animation: `${oneTimeSpin} 3s ease-out`,
            zIndex: -1,
          }}
        />
      )}

      {/* אפקטים מטורפים */}
      {showElements.effects && (
        <>
          <EnergyParticles />
        </>
      )}

      {/* כותרת */}
      {showElements.winner && (
        <Box
          sx={{
            position: "relative",
            mb: 4,
            zIndex: 20,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "4rem" },
              fontWeight: "bold",
              background: `
                linear-gradient(45deg,
                  #FFD700 0%,
                  #FF69B4 25%,
                  #00FFFF 50%,
                  #FF4500 75%,
                  #FFD700 100%)
              `,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              textShadow: "0 0 30px rgba(255, 215, 0, 0.5)",
              animation: `${fadeInUp} 1.5s ease-out, ${gentlePulse} 3s infinite`,
            }}
          >
            🏆 הקבוצות המנצחות 🏆
          </Typography>
        </Box>
      )}

      {/* כל הקבוצות במדרגות */}
      {showElements.winner && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 3,
            maxWidth: "1200px",
            width: "100%",
            zIndex: 15,
            mt: 4,
            px: 2,
          }}
        >
          {sortedTeams.map((team, index) => (
            <TeamCard
              key={team.id}
              team={team}
              position={index === 0 ? "winner" : "participant"}
              show={true}
              delay={index * 200}
              rank={index + 1}
              isTopWinner={index === 0}
              stepIndex={index}
            />
          ))}
        </Box>
      )}

      {/* אפקטים נוספים מסביב לקבוצה המנצחת */}
      {showElements.effects && (
        <>
          {/* זיקוקים מסביב */}
          {[...Array(12)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: `${15 + Math.random() * 70}%`,
                left: `${5 + Math.random() * 90}%`,
                animation: `${sparkle} ${3 + i * 0.2}s infinite`,
                animationDelay: `${i * 0.5}s`,
              }}
            >
              <AutoAwesome
                sx={{
                  fontSize: 25 + Math.random() * 15,
                  color: ["#FFD700", "#FF69B4", "#00FFFF", "#FF4500", "#32CD32"][i % 5],
                  filter: "drop-shadow(0 0 8px currentColor)",
                }}
              />
            </Box>
          ))}

          {/* כוכבים */}
          {[...Array(20)].map((_, i) => (
            <Box
              key={`star-${i}`}
              sx={{
                position: "absolute",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `${float} ${4 + i * 0.3}s infinite`,
              }}
            >
              <Star
                sx={{
                  fontSize: 12 + Math.random() * 10,
                  color: ["#FFD700", "#FF69B4", "#00FFFF", "#32CD32", "#FF4500"][i % 5],
                  filter: "drop-shadow(0 0 6px currentColor)",
                  animation: `${sparkle} 4s infinite`,
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            </Box>
          ))}
        </>
      )}
    </Box>
  )
}

// רכיב כרטיס קבוצה מטורף ומדהים – כל הכרטיסים באותו גודל

const TeamCard = ({ team, position, show, delay = 0, rank, isTopWinner = false, stepIndex = 0 }) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setIsVisible(true), delay)
      return () => clearTimeout(timer)
    }
  }, [show, delay])

  if (!isVisible) return null

  const rankConfig = {
    1: {
      medal: "🥇",
      label: "מקום ראשון",
      gradient: "linear-gradient(135deg, #FFD700 0%, #FFA500 40%, #FF8C00 100%)",
      border: "#FFD700",
      glow: "rgba(255, 215, 0, 0.6)",
      shine: "#fff8e1",
      bg: "rgba(255, 215, 0, 0.12)",
    },
    2: {
      medal: "🥈",
      label: "מקום שני",
      gradient: "linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 40%, #9E9E9E 100%)",
      border: "#C0C0C0",
      glow: "rgba(192, 192, 192, 0.5)",
      shine: "#f5f5f5",
      bg: "rgba(192, 192, 192, 0.1)",
    },
    3: {
      medal: "🥉",
      label: "מקום שלישי",
      gradient: "linear-gradient(135deg, #CD7F32 0%, #A0522D 40%, #8B4513 100%)",
      border: "#CD7F32",
      glow: "rgba(205, 127, 50, 0.5)",
      shine: "#fff3e0",
      bg: "rgba(205, 127, 50, 0.1)",
    },
  }

  const cfg = rankConfig[rank] || {
    medal: "🏅",
    label: `מקום ${rank}`,
    gradient: "linear-gradient(135deg, #4ECDC4 0%, #44A08D 40%, #2C7873 100%)",
    border: "#4ECDC4",
    glow: "rgba(78, 205, 196, 0.5)",
    shine: "#e0f7fa",
    bg: "rgba(78, 205, 196, 0.1)",
  }

  return (
    <Box
      sx={{
        animation: isTopWinner
          ? `${winnerEntrance} 2s ease-out`
          : `${epicEntrance} 1.5s ease-out`,
        zIndex: isTopWinner ? 20 : 10,
        flex: "0 0 auto",
      }}
    >
      <Box
        sx={{
          width: 260,
          borderRadius: "28px",
          overflow: "hidden",
          position: "relative",
          background: "rgba(15, 15, 35, 0.85)",
          backdropFilter: "blur(30px)",
          border: `2px solid ${cfg.border}55`,
          boxShadow: `
            0 0 40px ${cfg.glow},
            0 25px 50px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.15)
          `,
          transition: "all 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
          animation: isTopWinner ? `${winnerGlow} 4s infinite` : `${subtleGlow} 5s infinite`,
          "&:hover": {
            transform: "translateY(-16px) scale(1.03)",
            boxShadow: `
              0 0 80px ${cfg.glow},
              0 40px 80px rgba(0, 0, 0, 0.6),
              inset 0 1px 0 rgba(255, 255, 255, 0.2)
            `,
          },
        }}
      >
        {/* פס גרדיאנט עליון */}
        <Box sx={{ height: 5, background: cfg.gradient }} />

        {/* כותרת דרגה */}
        <Box
          sx={{
            textAlign: "center",
            pt: 3,
            pb: 2,
            position: "relative",
          }}
        >
          {/* מדליה */}
          <Typography
            sx={{
              fontSize: isTopWinner ? "4rem" : "3rem",
              lineHeight: 1,
              filter: `drop-shadow(0 4px 12px ${cfg.glow})`,
              animation: isTopWinner ? `${bounce} 3s infinite` : "none",
            }}
          >
            {cfg.medal}
          </Typography>
          <Typography
            sx={{
              color: cfg.border,
              fontWeight: 800,
              fontSize: "0.85rem",
              letterSpacing: "2px",
              textTransform: "uppercase",
              mt: 1,
              textShadow: `0 0 12px ${cfg.glow}`,
            }}
          >
            {cfg.label}
          </Typography>
        </Box>

        {/* שם הקבוצה */}
        <Box sx={{ textAlign: "center", px: 2, mb: 2 }}>
          <Typography
            sx={{
              fontSize: "1.6rem",
              fontWeight: 900,
              color: "#fff",
              textShadow: `0 0 20px ${cfg.glow}, 0 2px 4px rgba(0,0,0,0.5)`,
              letterSpacing: "0.5px",
              animation: isTopWinner ? `${textGlitch} 5s infinite` : "none",
            }}
          >
            {team.name}
          </Typography>
        </Box>

        {/* ניקוד */}
        <Box
          sx={{
            mx: 2.5,
            mb: 2.5,
            p: 2,
            borderRadius: "16px",
            background: cfg.bg,
            border: `1px solid ${cfg.border}30`,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* shimmer על הניקוד */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(90deg, transparent, ${cfg.shine}22, transparent)`,
              backgroundSize: "200% 100%",
              animation: `${shimmer} 3s infinite`,
            }}
          />
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", fontWeight: 600, mb: 0.5, letterSpacing: "1px" }}>
            ניקוד כללי
          </Typography>
          <Typography
            sx={{
              fontSize: "2.5rem",
              fontWeight: 900,
              background: cfg.gradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: `drop-shadow(0 0 8px ${cfg.glow})`,
              lineHeight: 1.2,
            }}
          >
            {(team.total_score || team.score || 0).toLocaleString("he-IL")}
          </Typography>
        </Box>

        {/* שחקן מוביל */}
        {team.top_player && (
          <Box
            sx={{
              mx: 2.5,
              mb: 3,
              p: 1.5,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem", fontWeight: 600, mb: 0.5 }}>
              ⭐ שחקן מוביל
            </Typography>
            <Typography sx={{ color: "#fff", fontSize: "1.05rem", fontWeight: 700 }}>
              {team.top_player.name || team.top_player.phone}
            </Typography>
            <Typography sx={{ color: cfg.border, fontSize: "0.85rem", fontWeight: 600, mt: 0.3 }}>
              {team.top_player.point || 0} נקודות
            </Typography>
          </Box>
        )}

        {/* פס גרדיאנט תחתון */}
        <Box sx={{ height: 3, background: cfg.gradient, opacity: 0.5 }} />
      </Box>
    </Box>
  )
}

// רכיב הדף העולה
const RisingPage = ({ team, show }) => {
  const [isRising, setIsRising] = useState(false)
  const [displayedText, setDisplayedText] = useState("")
  const [showFullContent, setShowFullContent] = useState(false)
  const fullText = "הקבוצות המנצחות"

  useEffect(() => {
    if (show) {
      setIsRising(true)
      const typingStartDelay = 1000
      const typingSpeed = 200
      let typingInterval
      let revealTimer

      const startTyping = () => {
        let i = 0
        typingInterval = setInterval(() => {
          if (i < fullText.length) {
            setDisplayedText((prev) => prev + fullText.charAt(i))
            i++
          } else {
            clearInterval(typingInterval)
              setShowFullContent(true)

          }
        }, typingSpeed)
      }

      const typingDelayTimer = setTimeout(startTyping, typingStartDelay)

      return () => {
        clearTimeout(typingDelayTimer)
        clearInterval(typingInterval)
        clearTimeout(revealTimer)
        setDisplayedText("")
        setShowFullContent(false)
        setIsRising(false)
      }
    } else {
      setDisplayedText("")
      setShowFullContent(false)
      setIsRising(false)
    }
  }, [show])

  if (!show && !isRising) return null

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: `
          linear-gradient(135deg, 
            #1e3c72 0%, 
            #2a5298 20%, 
            #667eea 40%, 
            #764ba2 60%, 
            #f093fb 80%,
            #ff6b9d 100%)
        `,
        zIndex: 180,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: isRising ? `${pageRise} 2s ease-out forwards` : "none",
        overflow: "hidden",
      }}
    >
      {!showFullContent && (
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "2.5rem", md: "4rem" },
            fontWeight: "bold",
            color: "#FFD700",
            textShadow: "4px 4px 8px rgba(0,0,0,0.5)",
            textAlign: "center",
            animation: `${glow} 2s infinite`,
            opacity: displayedText.length > 0 ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          {displayedText}
        </Typography>
      )}

      {showFullContent && <WinningTeamContent teams={team} show={true} />}
    </Box>
  )
}

// רצף קרדיטים בסיום — עולה מלמטה (בלי השהיה ריקה בהתחלה)
const creditsRise = keyframes`
  0% { transform: translateY(55%); opacity: 0; }
  12% { opacity: 1; }
  100% { transform: translateY(0); opacity: 1; }
`

function Credits({ show }) {
  if (!show) return null
  const F = "'Assistant', 'Segoe UI', sans-serif"
  const sparkles = [
    { top: "12%", left: "18%", s: 8, d: "0s" }, { top: "20%", left: "84%", s: 6, d: ".6s" },
    { top: "72%", left: "12%", s: 10, d: "1.1s" }, { top: "80%", left: "80%", s: 7, d: ".3s" },
    { top: "42%", left: "7%", s: 5, d: ".9s" }, { top: "55%", left: "92%", s: 9, d: "1.4s" },
    { top: "14%", left: "48%", s: 6, d: ".2s" }, { top: "90%", left: "44%", s: 8, d: "1.7s" },
  ]
  return (
    <Box sx={{
      position: "fixed", inset: 0, zIndex: 300, overflow: "hidden", fontFamily: F,
      background: "radial-gradient(circle at 28% 18%, #26306e 0%, transparent 55%), radial-gradient(circle at 78% 82%, #3a1a5e 0%, transparent 55%), linear-gradient(160deg,#0b1330 0%,#141b46 50%,#231452 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {/* זוהרים מרחפים */}
      <Box sx={{ position: "absolute", width: 340, height: 340, borderRadius: "50%", top: -90, right: -70, background: "radial-gradient(circle, rgba(124,255,183,.18), transparent 70%)", animation: `${float} 7s ease-in-out infinite` }} />
      <Box sx={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", bottom: -110, left: -90, background: "radial-gradient(circle, rgba(255,215,0,.14), transparent 70%)", animation: `${float} 9s ease-in-out infinite` }} />
      {/* ניצוצות */}
      {sparkles.map((sp, i) => (
        <Box key={i} sx={{ position: "absolute", top: sp.top, left: sp.left, width: sp.s, height: sp.s, borderRadius: "50%", background: "#fff", boxShadow: "0 0 12px 2px rgba(255,255,255,.85)", animation: `${sparkle} 2.4s ease-in-out ${sp.d} infinite` }} />
      ))}
      {/* כרטיס זכוכית עולה */}
      <Box sx={{
        position: "relative", animation: `${creditsRise} 6s ease-out forwards`, textAlign: "center", color: "#fff",
        px: { xs: 4, md: 8 }, py: { xs: 5, md: 6 }, borderRadius: 6, maxWidth: 640, mx: 2,
        background: "rgba(255,255,255,0.06)", backdropFilter: "blur(14px)",
        border: "1px solid rgba(255,255,255,0.18)", boxShadow: "0 24px 70px rgba(0,0,0,.55), inset 0 1px 1px rgba(255,255,255,.25)",
      }}>
        {/* פס קישוט זוהר */}
        <Box sx={{ width: 90, height: 6, mx: "auto", mb: 3.5, borderRadius: 3, background: "linear-gradient(90deg,#7CFFB7,#FFD700)", boxShadow: "0 0 22px rgba(124,255,183,.7)", animation: `${gentlePulse} 2.5s infinite` }} />
        <Typography sx={{ fontFamily: F, fontSize: { xs: "2.4rem", md: "3.4rem" }, fontWeight: 800, color: "#FFD700", textShadow: "0 3px 14px rgba(0,0,0,.4)", mb: 1.5, lineHeight: 1.15 }}>
          תודה ששיחקתם איתנו!
        </Typography>
        <Typography sx={{ fontFamily: F, fontSize: { xs: "1.15rem", md: "1.5rem" }, fontWeight: 600, opacity: .92 }}>
          נהניתם? ספרו לחברים ושתפו את המשחק
        </Typography>
        {/* מפריד מנצנץ */}
        <Box sx={{ my: 3.5, height: 2, width: "72%", mx: "auto", borderRadius: 2, background: "linear-gradient(90deg,transparent,#7CFFB7,transparent)", animation: `${shimmer} 3s infinite` }} />
        <Typography sx={{ fontFamily: F, fontSize: { xs: "2.1rem", md: "2.9rem" }, fontWeight: 900, mb: .3,
          background: "linear-gradient(135deg,#7CFFB7,#FFD700)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
          מיני קליק
        </Typography>
        <Typography sx={{ fontFamily: F, fontSize: { xs: "1.05rem", md: "1.3rem" }, fontWeight: 500, opacity: .85, mb: 3.5 }}>
          חוויית משחק שלא הכרתם
        </Typography>
        {/* פרטי קשר */}
        <Box sx={{ display: "inline-flex", flexDirection: "column", gap: 1, alignItems: "center", px: 4, py: 2, borderRadius: 4, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)" }}>
          <Box component="a" href="https://clickerim.teletop.biz" target="_blank" rel="noreferrer"
            sx={{ fontFamily: F, fontSize: { xs: "1.2rem", md: "1.55rem" }, fontWeight: 700, color: "#7CFFB7", textDecoration: "none" }}>
            clickerim.teletop.biz
          </Box>
          <Typography sx={{ fontFamily: F, fontSize: { xs: "1.45rem", md: "1.9rem" }, fontWeight: 800, letterSpacing: 2, direction: "ltr" }}>
            077-202-7575
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default function WinnersPage() {
  const [showCelebration, setShowCelebration] = useState(false)
  const [showTitle, setShowTitle] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [showChaos, setShowChaos] = useState(false)
  const [podiumsFalling, setPodiumsFalling] = useState(false)
  const [showRisingPage, setShowRisingPage] = useState(false)
  const [winningTeam, setWinningTeam] = useState(null)
  const [winners, setWinners] = useState([])
  const [showCredits, setShowCredits] = useState(false)

  useEffect(() => {
    if (showCelebration) {
      if (window.currentAudio) {
        try {
          window.currentAudio.pause()
          window.currentAudio.currentTime = 0
        } catch (e) {}
      }
      const audio = new Audio("/music/winners.mp3")
      audio.volume = 0.6
      audio.play().catch(console.error)
      window.currentAudio = audio
    }
  }, [showCelebration])

  const defaultWinners = [
    {
      id: 1,
      name: "",
      score: 0,
      phone: "",
      position: 1,
      color: "#FFD700",
    },
    {
      id: 2,
      name: "",
      score: 0,
      phone: "",
      position: 2,
      color: "#C0C0C0",
    },
    {
      id: 3,
      name: "",
      score: 0,
      phone: "",
      position: 3,
      color: "#CD7F32",
    },
  ]

useEffect(() => {
  const gameId = window.location.pathname.split("/").pop();
  const titleTimer = setTimeout(() => setShowTitle(true), 500);
  const celebrationTimer = setTimeout(() => setShowCelebration(true));
  const statsTimer = setTimeout(() => setShowStats(true), 3500);
  const chaosStartTimer = setTimeout(() => setShowChaos(true), 17000);
  const shakeEndTimer = setTimeout(() => setShowChaos(false), 22000);
  const podiumsFallTimer = setTimeout(() => setPodiumsFalling(true), 19000);
  const risingPageAppearTimer = setTimeout(() => setShowRisingPage(true), 22000);
  const creditsTimer = setTimeout(() => setShowCredits(true), 46000);

  // טעינת נתוני המנצחים
  fetch(`${API_URL}/get_w/${gameId}`)
    .then((res) => res.json())
    .then((data) => {
      console.log("✅ data from server:", data);
      const formatted = data.map((item) => ({
        id: item.id,
        name: item.name || item.phone || "",
        score: item.point || 0,
        clicks: item.quick?.toLocaleString() || "0",
        phone: item.phone || "",
        position: item.id,
        color: item.id === 1 ? "#FFD700" : item.id === 2 ? "#C0C0C0" : "#CD7F32",
        trophyColor: item.id === 1 ? "#FFD700" : item.id === 2 ? "#C0C0C0" : "#CD7F32",
        gradient:
          item.id === 1
            ? "linear-gradient(135deg, #FFD700, #FFA500, #FF8C00)"
            : item.id === 2
              ? "linear-gradient(135deg, #C0C0C0, #A8A8A8, #909090)"
              : "linear-gradient(135deg, #CD7F32, #B8860B, #A0522D)",
      }));
      setWinners(formatted);
      console.log("🚀 Winners Set:", formatted);
    })
    .catch(console.error);

  // טעינת קבוצות מנצחות
  fetch(`${API_URL}/get_teams/${gameId}`)
    .then((res) => res.json())
    .then((data) => {
      if (Array.isArray(data) && data.length > 0) {
        setWinningTeam(data);
      } else if (data && data.name) {
        setWinningTeam(false);
      }
    })
    .catch(console.error);

  return () => {
    clearTimeout(titleTimer);
    clearTimeout(celebrationTimer);
    clearTimeout(statsTimer);
    clearTimeout(chaosStartTimer);
    clearTimeout(shakeEndTimer);
    clearTimeout(podiumsFallTimer);
    clearTimeout(risingPageAppearTimer);
    clearTimeout(creditsTimer);
  };
}, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        fontFamily: "'Orbitron', sans-serif",
        background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #667eea 50%, #764ba2 75%, #f093fb 100%)",
        position: "relative",
        overflow: "hidden",
        py: 6,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at center, rgba(255,255,255,0.05), transparent 70%)`,
          filter: "blur(12px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <>
        <LightBeam left color="rgba(255, 255, 0, 0.18)" />
        <LightBeam left={false} color="rgba(255, 255, 0, 0.18)" />
      </>

      {/* אפקט כוכבים */}
      <SparkleEffect intense={showChaos} />

      {/* זיקוקים רגילים */}
      {showCelebration && (
        <>
          <Box sx={{ position: "absolute", top: "8%", left: "8%", zIndex: 5 }}>
            <Firework delay={0} />
          </Box>
          <Box sx={{ position: "absolute", top: "12%", right: "12%", zIndex: 5 }}>
            <Firework delay={600} />
          </Box>
          <Box sx={{ position: "absolute", top: "18%", left: "85%", zIndex: 5 }}>
            <Firework delay={1200} />
          </Box>
          <Box sx={{ position: "absolute", top: "25%", left: "15%", zIndex: 5 }}>
            <Firework delay={1800} />
          </Box>
          <Box sx={{ position: "absolute", top: "30%", right: "20%", zIndex: 5 }}>
            <Firework delay={2400} />
          </Box>
        </>
      )}

      {/* כאוס וזיקוקים אינטנסיביים */}
      {showChaos && (
        <>
          <BigExplosion delay={0} />
          <BigExplosion delay={1000} />
          <BigExplosion delay={2000} />
          {[...Array(20)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: `${Math.random() * 80}%`,
                left: `${Math.random() * 80}%`,
                zIndex: 50,
              }}
            >
              <Firework delay={i * 200} size={50} intense />
            </Box>
          ))}
        </>
      )}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 10 }}>
        {/* כותרת מתקדמת */}
        <Box
          sx={{
            textAlign: "center",
            mb: 8,
            animation: showTitle ? `${fadeInUp} 1.5s ease-out` : "none",
            opacity: showTitle ? 1 : 0,
            position: "relative",
            zIndex: 99,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              p: 1,
              borderRadius: 5,
              color: "white",
              fontWeight: "bold",
              textShadow: "3px 3px 6px rgba(0,0,0,0.5)",
              mb: 2,
              animation: `${glow} 3s infinite`,
            }}
          >
            🏆 אלופי הקליקרים 🏆
          </Typography>
        </Box>

        {/* הפודיום */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "end",
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 15,
            animation: `${riseUp} 1.2s ease-out`,
            pb: { xs: -2, sm: -4, md: -6 },
          }}
        >
          <>
            {/* מקום שני */}
            <Box sx={{ zIndex: 1, mt: 6, mr: -4 }}>
              <PodiumStep
                winner={winners[1] || defaultWinners[1]}
                height={{ xs: 100, sm: 140, md: 180 }}
                delay={7000}
                shouldFall={podiumsFalling}
              />
            </Box>
            {/* מקום ראשון */}
            <Box sx={{ zIndex: 3, position: "relative" }}>
              <PodiumStep
                winner={winners[0] || defaultWinners[0]}
                height={{ xs: 160, sm: 200, md: 240 }}
                delay={12000}
                shouldFall={podiumsFalling}
              />
            </Box>
            {/* מקום שלישי */}
            <Box sx={{ zIndex: 1, mt: 6, ml: -4 }}>
              <PodiumStep
                winner={winners[2] || defaultWinners[2]}
                height={{ xs: 100, sm: 140, md: 180 }}
                delay={2000}
                shouldFall={podiumsFalling}
              />
            </Box>
          </>
        </Box>
      </Container>

      {/* הקבוצות המנצחות — עיצוב פודיום הזכוכית, פרימיום מוגבר */}
      {showRisingPage && winningTeam && winningTeam.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 40,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            backgroundColor: "#050a18",
            background: `
              radial-gradient(ellipse 70% 50% at 50% 82%, rgba(255,190,40,.14) 0%, transparent 60%),
              radial-gradient(ellipse 90% 55% at 50% -12%, rgba(30,58,120,0.55) 0%, transparent 60%),
              radial-gradient(ellipse 130% 100% at 50% 0%, #0e1b3c 0%, #0a1430 38%, #070e22 68%, #050a18 100%)
            `,
          }}
        >
          {/* זוהרים מרחפים לתחושת אלופים */}
          <Box sx={{ position: "absolute", width: 380, height: 380, borderRadius: "50%", top: "8%", left: "-6%", background: "radial-gradient(circle, rgba(255,200,60,.16), transparent 70%)", filter: "blur(6px)", animation: `${float} 8s ease-in-out infinite`, pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", width: 420, height: 420, borderRadius: "50%", bottom: "2%", right: "-8%", background: "radial-gradient(circle, rgba(90,150,255,.16), transparent 70%)", filter: "blur(8px)", animation: `${float} 10s ease-in-out infinite`, pointerEvents: "none" }} />
          {["10%","22%","78%","88%","46%"].map((left, i) => (
            <Box key={i} sx={{ position: "absolute", top: `${12 + i * 15}%`, left, fontSize: { xs: "1rem", md: "1.4rem" }, opacity: 0.8, filter: "drop-shadow(0 0 6px rgba(255,215,0,.7))", animation: `${float} ${6 + i}s ease-in-out infinite`, pointerEvents: "none" }}>✨</Box>
          ))}

          {/* כותרת — למעלה, לא נדבקת לקבוצות */}
          <Box sx={{ textAlign: "center", pt: { xs: 3, md: 5 }, flexShrink: 0, zIndex: 1 }}>
            <Box sx={{ fontSize: { xs: "2.6rem", md: "3.6rem" }, lineHeight: 1, mb: 0.3, filter: "drop-shadow(0 6px 16px rgba(255,180,0,.6))" }}>🏆</Box>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "2.3rem", md: "3.6rem" },
                letterSpacing: "1px",
                background: "linear-gradient(180deg, #fff7dd 0%, #ffe08a 55%, #ffb300 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0 4px 12px rgba(120,80,0,.6))",
              }}
            >
              הקבוצות המנצחות
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 1 }}>
              <Box sx={{ height: 2, width: { xs: 40, md: 90 }, background: "linear-gradient(90deg, transparent, #FFC107)" }} />
              <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.2rem", md: "1.6rem" }, letterSpacing: "2px", background: "linear-gradient(135deg, #FFE55C, #FFB300)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>קליקרים</Typography>
              <Box sx={{ height: 2, width: { xs: 40, md: 90 }, background: "linear-gradient(90deg, #FFC107, transparent)" }} />
            </Box>
          </Box>

          {/* פודיום הזכוכית — ממורכז במרחב שנשאר מתחת לכותרת */}
          <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
            <TeamsPodiumScreen teams={winningTeam} />
          </Box>
        </Box>
      )}

      <Credits show={showCredits} />
    </Box>
  )
}
